/// <reference types="gtag.js" />

// Minimal GA4 loader + event helper (with queue)
// Usage:
//   initAnalytics('G-XXXX...');
//   track('booking_started', { account_id: 'acme' });

import { ClientPortalConfig } from "types/generated/models";

type InitOptions = {
    sendPageView?: boolean;
    /**
     * Maps to GA4 `debug_mode`. Keep `false` in production so customer events
     * appear in standard reports. Set `true` only for local or non-production
     * sessions so traffic is isolated in DebugView.
     */
    debug?: boolean;
    defaultParams?: Record<string, unknown>;
};

interface GtagFunction extends Gtag.Gtag {
    q?: unknown[];
}

declare global {
    interface Window {
        gtag?: GtagFunction;
    }

    var ga4LoadedId: string | null | undefined;
    var ga4Ready: boolean | undefined;
    var ga4Queue: Array<{ name: string; params: Record<string, unknown> }> | undefined;
    var dataLayer: unknown[] | undefined;
}

function ensureGlobals() {
    if (!globalThis.ga4Queue) globalThis.ga4Queue = [];
    globalThis.ga4Ready ??= false;
    if (!globalThis.ga4LoadedId) globalThis.ga4LoadedId = null;
}

function ensureDataLayer() {
    globalThis.dataLayer = globalThis.dataLayer || [];
    if (!globalThis.gtag) {
        const gtagFn: GtagFunction = function gtag() {
            // eslint-disable-next-line prefer-rest-params
            globalThis.dataLayer.push(arguments);
        };
        gtagFn.q = [];
        globalThis.gtag = gtagFn;
    }
}

function flushQueue() {
    if (!globalThis.ga4Ready || !globalThis.ga4LoadedId || !globalThis.gtag || !globalThis.ga4Queue) return;

    const gtag = globalThis.gtag;
    for (const { name, params } of globalThis.ga4Queue) {
        gtag("event", name, params || {});
    }
    globalThis.ga4Queue.length = 0;
}

/**
 * Initializes Google Analytics 4 (gtag.js) for the Client Portal.
 *
 * Loads the GA4 script once per measurement ID. Subsequent calls with the same
 * ID are no-ops.
 *
 * Accepts the GA4 cross-domain linker (`_gl`) so Client Portal reuses the
 * referring site's Client ID instead of minting a new one, keeping ad-click
 * attribution intact for events such as `booking_completed`.
 *
 * Debug mode (`opts.debug`) is sent as GA4 `debug_mode`. Leave it `false` in
 * production so real customer events (including completed bookings) are captured
 * in standard reports. Enable it only in non-production or internal test
 * sessions so events show in DebugView instead of production reporting.
 *
 * @param measurementId - GA4 measurement ID (e.g. `G-XXXX...`).
 * @param opts - Optional init settings.
 * @param opts.sendPageView - Whether to send an automatic page_view on config. Defaults to `true`.
 * @param opts.debug - When `true`, tags all events as debug traffic. Defaults to `false`.
 *   Enable only for local / non-production environments.
 * @param opts.defaultParams - Extra parameters merged into the GA4 config call.
 */
export function initAnalytics(measurementId: string, opts: InitOptions = {}) {
    ensureGlobals();
    if (globalThis.ga4Ready && globalThis.ga4LoadedId === measurementId) return;

    ensureDataLayer();
    const { sendPageView = true, debug = false, defaultParams = {} } = opts;

    globalThis.gtag("js", new Date());
    globalThis.gtag("config", measurementId, {
        send_page_view: sendPageView,
        debug_mode: debug,
        ...defaultParams,
        linker: { accept_incoming: true },
    });

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    s.onload = () => {
        globalThis.ga4LoadedId = measurementId;
        globalThis.ga4Ready = true;
        flushQueue();
    };
    s.onerror = () => {
        console.error("GA4 script failed to load");
    };

    document.head.appendChild(s);
}

export function track(eventName: string, params: Record<string, unknown> = {}) {
    ensureGlobals();
    if (!globalThis.ga4Ready || !globalThis.ga4LoadedId) {
        globalThis.ga4Queue.push({ name: eventName, params });
        return;
    }
    globalThis.gtag("event", eventName, params);
}

type ClientPortalContext = {
    clientPortalConfig?: ClientPortalConfig | null;
    clientPortalGuid?: string;
};

export function trackEvent(eventName: string, params: Record<string, unknown> = {}, context?: ClientPortalContext) {
    const enrichedParams: Record<string, unknown> = {
        ...params,
        page_location: globalThis.location.href,
        page_path: globalThis.location.pathname,
    };

    if (context?.clientPortalGuid) {
        enrichedParams.client_portal_guid = context.clientPortalGuid;
    }

    if (context?.clientPortalConfig) {
        const config = context.clientPortalConfig;

        enrichedParams.user_logged_in = config.userLoggedIn ?? false;

        if (config.userLoggedIn && config.userDetails?.clientId) {
            enrichedParams.user_id = config.userDetails.clientId;
        }

        if (config.clientPortalName) {
            enrichedParams.portal_name = config.clientPortalName;
        }
    }

    track(eventName, enrichedParams);
}
